import createSelector from 'lib/web/createSelector'
import { isBedbankItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { checkoutAccommodationOfferView } from './view/accommodation'
import { getFlightItems } from './view/flights'
import { getVillaViews } from 'checkout/selectors/view/villa'
import { isActiveSubscribedLuxPlusMember, isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getTourV1Items } from './view/tourv1'
import { getOptimizelyExperimentVariation, OptimizelyExperiments } from 'lib/optimizely/optimizelyUtils'
import { isBedbank, isLEHotel } from 'lib/offer/offerTypes'
import { dateDifference } from 'lib/datetime/dateUtils'

export const isFlightsPendingToAddToCart = createSelector(
  getFlightItems,
  (flightItems) : boolean => {
    return flightItems.length > 0 &&
    flightItems.some(item => {
      if (item.fareType === 'return') {
        return !item.departing || !item.returning
      } else if (item.fareType === 'oneWay') {
        return !item.departing
      }
    })
  },
)

export const hasIncompleteTourItem = createSelector(
  getTourV1Items,
  item => item.some(item => !item.packageId || !item.startDate || !item.endDate),
)

export const isExperiencesSupportedToAddToCart = createSelector(
  checkoutAccommodationOfferView,
  getVillaViews,
  (state: App.State) => state.checkout.cart.postPurchase,
  (state: App.State) => !!getOptimizelyExperimentVariation(state, OptimizelyExperiments.disabledInflowExperiences),
  (state: App.State) => state.geo.currentRegionCode,
  (accommodationViews, villaViews, postPurchase, disabledExperimentOn, currentRegionCode) : boolean => {
    if (!postPurchase || postPurchase === 'select-date') {
      if (accommodationViews.data.length > 0 || villaViews.data.length > 0) {
        let disabledByExperiement = false
        if (disabledExperimentOn) {
          disabledByExperiement = accommodationViews.data.some(view => {
            let disabled = false
            if (isLEHotel(view.offer)) {
              // same country as current country
              disabled = view.offer.property.geoData.countryCode?.toLowerCase() === currentRegionCode.toLowerCase()
            } else if (isBedbank(view.offer)) {
              disabled = view.offer.property.address.countryCode.toLowerCase() === currentRegionCode.toLowerCase()
            }

            if (view.startDate) {
              // 240 days in the future
              disabled = disabled || dateDifference(new Date(view.startDate)).days > 240
            }
            return disabled
          })

          // check villas as well
          disabledByExperiement = disabledByExperiement || villaViews.data.some(view => {
            return view.offer.property.geoData.countryCode?.toLowerCase() === currentRegionCode.toLowerCase() ||
            dateDifference(new Date(view.item.checkIn)).days > 240
          })
        }

        return !disabledByExperiement && (
          accommodationViews.data.every(view => view.itemViews.every(itemView => isInstantBookingLEHotelItem(itemView.item) || isBedbankItem(itemView.item))) ||
          villaViews.data.length > 0
        )
      }
    }

    return false
  },
)

export const isLuxPlusAvailableToAddToCart = createSelector(
  isLuxPlusEnabled,
  isActiveSubscribedLuxPlusMember,
  (state: App.State) => state.auth.account.subscriberTier,
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => state.checkout.cart.postPurchase,
  (enabled, activeSubscribedMember, subscriberTier, isGift, isPostPurchase): boolean => {
    const validSubscriberTier = subscriberTier === 'free_preview' || subscriberTier === null
    return !!(enabled && !activeSubscribedMember && !isGift && validSubscriberTier && !isPostPurchase)
  },
)
